<template>
  <div class="go-nogo">
    <OpportunityHeader />
    <BaseLoadingBar v-if="pending" />

    <div class="go-nogo__main" v-else>
      <FinalDecisionCard
        v-if="showFinalDecisionCard"
        @record="openFinalDecisionDialog"
        @change="openFinalDecisionDialog"
        @delete="confirmDeleteFinalDecision"
      />

      <!-- Overview card — title, progress, vote actions -->
      <div class="go-nogo__overview" v-if="!secondaryVotesLocked">
        <div class="go-nogo__overview-header">
          <h2 class="go-nogo__title">{{ $t('go-nogo.title') }}</h2>
        </div>

        <GoNoGoVoteProgress
          :go-percentage="goPercentage"
          :nogo-percentage="nogoPercentage"
          :votes-count="votesCount"
          :total-votes="totalVotes"
        />

        <div class="go-nogo__vote-buttons">
          <BaseVoteButton @click.native="openVote()">
            {{
              currentUserVote && currentUserVote.isPending
                ? $t('go-nogo.vote')
                : currentUserVote
                ? $t('go-nogo.change-vote')
                : $t('go-nogo.vote')
            }}
          </BaseVoteButton>
          <BaseVoteButton @click.native="deleteVote()" v-if="currentUserVote && !currentUserVote.isPending">
            {{ $t('go-nogo.delete-vote') }}
          </BaseVoteButton>
        </div>
      </div>

      <!-- Votes card — list + empty state + invitation -->
      <div class="go-nogo__votes-card">
        <div class="go-nogo__votes-header" :class="{ 'go-nogo__votes-header--flush': secondaryVotesLocked }">
          <h2 class="go-nogo__title">{{ $t('go-nogo.votes-section-title') }}</h2>
          <div class="go-nogo__container" v-if="!secondaryVotesLocked">
            <BaseInvitationGoNoGo
              :value="assignees"
              :opportunityVotes="filteredVotes(opportunityVotes)"
              :opportunityId="opportunityId"
              :attach="'.go-nogo__container'"
              tooltipPlacement="bottom"
            />
          </div>
        </div>

        <div v-if="secondaryVotesLocked" class="go-nogo__votes-progress">
          <GoNoGoVoteProgress
            :go-percentage="goPercentage"
            :nogo-percentage="nogoPercentage"
            :votes-count="votesCount"
            :total-votes="totalVotes"
          />
        </div>

        <div v-if="sortedVotes.length === 0" class="go-nogo__empty">
          <div class="go-nogo__empty-icon">
            <BaseIcon name="user" />
          </div>
          <div class="go-nogo__empty-body">
            <span class="go-nogo__empty-title">{{ $t('go-nogo.empty-title') }}</span>
            <span class="go-nogo__empty-subtitle">{{ $t('go-nogo.empty') }}</span>
          </div>
        </div>

        <div v-else class="go-nogo__items">
          <div class="go-nogo__item" v-for="(opportunityVote, index) in sortedVotes" :key="index">
            <div class="go-nogo__item--vote">
              <div class="go-nogo__item--avatar">
                <BaseAvatar :name="voterDisplayName(opportunityVote.user)" />
                <div class="go-nogo__item--user-info">
                  <span v-if="opportunityVote.user" class="go-nogo__item--user-name-row">
                    <!-- Email on hover: it's what separates two users sharing a display name. -->
                    <span
                      class="go-nogo__item--user-name"
                      :class="{ 'go-nogo__item--user-name--current': isCurrentUser(opportunityVote.user) }"
                      v-tooltip="{ content: opportunityVote.user.email, placement: 'top', offset: '8px' }"
                    >
                      {{ getFullName(opportunityVote.user.firstName, opportunityVote.user.lastName) }}
                    </span>
                    <BaseTag v-if="isCurrentUser(opportunityVote.user)" preset="secondary" tone="neutral">
                      {{ $t('shared.me') }}
                    </BaseTag>
                  </span>
                  <span v-else>{{ $t('unknown.user') }}</span>
                  <span class="go-nogo__item--vote-date">{{ voteDateLabel(opportunityVote) }}</span>
                </div>
              </div>
              <div class="go-nogo__item--votes">
                <div v-if="opportunityVote.vote" class="go-nogo__vote-display">
                  <BaseTag preset="soft" :tone="getGoNoGoVoteTone(opportunityVote.vote)">
                    {{ $t(`go-nogo.${opportunityVote.vote.toLowerCase()}`) }}
                  </BaseTag>
                </div>
                <div v-else class="go-nogo__vote-display">
                  <BaseTag preset="soft" tone="neutral">{{ $t('go-nogo.pending') }}</BaseTag>
                </div>
                <!-- Phones: in a sheet; an invitation that can't be removed has no ⋯ (the desktop's
                     greyed one). -->
                <template v-if="isPhone">
                  <BaseMoreActions
                    v-if="!secondaryVotesLocked && canRemoveInvitation(opportunityVote)"
                    :title="
                      opportunityVote.user
                        ? getFullName(opportunityVote.user.firstName, opportunityVote.user.lastName)
                        : $t('unknown.user')
                    "
                    :actions="invitationActions"
                    @select="removeInvitation(opportunityVote)"
                  />
                </template>
                <v-menu v-else-if="!secondaryVotesLocked" offset-y left>
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      v-if="!canRemoveInvitation(opportunityVote)"
                      v-bind="attrs"
                      class="go-nogo__item--more"
                      @click.stop
                    >
                      <BaseIcon name="more-horizontal" class="go-nogo__item--more-icon disabled" />
                    </div>
                    <div v-else v-bind="attrs" v-on="on" class="go-nogo__item--more" @click.stop>
                      <BaseIcon name="more-horizontal" class="go-nogo__item--more-icon" />
                    </div>
                  </template>
                  <div class="base-actions-menu">
                    <ul class="base-actions-menu__ul">
                      <li class="base-actions-menu__li delete" @click="removeInvitation(opportunityVote)">
                        <BaseIcon name="trash-2" />
                        {{ $t('go-nogo.remove-invitation') }}
                      </li>
                    </ul>
                  </div>
                </v-menu>
              </div>
            </div>
            <div v-if="opportunityVote.vote && hasVoteReasons(opportunityVote)" class="go-nogo__item--justification">
              <p
                v-if="getCustomGoNoGoReason(opportunityVote)"
                class="go-nogo__custom-reason"
                :class="`go-nogo__custom-reason--${opportunityVote.vote.toLowerCase()}`"
              >
                {{ getCustomGoNoGoReason(opportunityVote) }}
              </p>
              <div v-if="getPresetVoteReasons(opportunityVote).length" class="go-nogo__preset-reasons">
                <BaseChip
                  v-for="(presetReason, reasonIndex) in getPresetVoteReasons(opportunityVote)"
                  :key="`preset-${reasonIndex}`"
                  size="sm"
                  preset="soft"
                  :tone="getGoNoGoVoteTone(opportunityVote.vote)"
                  :interactive="false"
                >
                  {{ presetReason }}
                </BaseChip>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <GoNoGoDialog
      ref="goNoGoDialog"
      :userVote="currentUserVote"
      :changeVote="currentUserVote && currentUserVote.isPending ? false : currentUserVote ? true : false"
      :opportunityId="opportunityId"
    />
    <ConfirmationDialog
      ref="confirmationDialog"
      :buttonText="$t('go-nogo.delete-dialog.remove')"
      @execute="executeDeleteVote"
    >
      <template slot="content">
        <div class="confirmation-dialog__content">
          <i18n path="go-nogo.delete-dialog.content">
            <template v-slot:action>
              <span class="confirmation-dialog__action">{{ $t('go-nogo.delete-dialog.remove') }}</span>
            </template>
          </i18n>
        </div>
      </template>
    </ConfirmationDialog>

    <FinalDecisionDialog
      ref="finalDecisionDialog"
      :finalDecision="finalDecision"
      :submitting="submittingFinalDecision"
      @confirm="submitFinalDecision"
    />
    <ConfirmationDialog
      ref="deleteFinalDecisionDialog"
      isDanger
      :buttonText="$t('go-nogo.final-decision.delete-dialog.remove')"
      @execute="executeDeleteFinalDecision"
    >
      <template slot="content">
        <div class="confirmation-dialog__content">
          <i18n path="go-nogo.final-decision.delete-dialog.content">
            <template v-slot:action>
              <span class="confirmation-dialog__action">
                {{ $t('go-nogo.final-decision.delete-dialog.remove') }}
              </span>
            </template>
          </i18n>
          <div class="dialog__warning-notice">{{ $t('go-nogo.final-decision.delete-dialog.warning') }}</div>
          <div class="go-nogo__prefer-change">{{ $t('go-nogo.final-decision.delete-dialog.prefer-change') }}</div>
        </div>
      </template>
    </ConfirmationDialog>
  </div>
</template>

<script>
import BaseAvatar from '@/components/base/BaseAvatar.vue';
import BaseChip from '@/components/base/BaseChip.vue';
import BaseIcon from '@/components/base/BaseIcon';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import BaseLoadingBar from '@/components/base/BaseLoadingBar.vue';
import BaseInvitationGoNoGo from '@/components/base/BaseInvitationGoNoGo';
import BaseVoteButton from '@/components/base/BaseVoteButton.vue';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog';
import FinalDecisionCard from '@/components/page-go-nogo/FinalDecisionCard';
import FinalDecisionDialog from '@/components/page-go-nogo/FinalDecisionDialog';
import GoNoGoDialog from '@/components/page-go-nogo/GONoGoDialog';
import GoNoGoVoteProgress from '@/components/page-go-nogo/GoNoGoVoteProgress';
import OpportunityHeader from '@/components/page-opportunities/OpportunityHeader';

import { getGoNoGoVoteTone } from '@/util/tag-tones/go-nogo-tag';
import { formatCustomVoteReasons, hasVoteReasons, partitionVoteReasons } from '@/util/go-nogo-vote-reasons';
import { formatLongDate } from '@/util/date-util';
import {
  FINAL_DECISION_ALREADY_EXISTS,
  FINAL_DECISION_NOT_FOUND,
  SECONDARY_VOTES_LOCKED,
} from '@/util/go-nogo-final-decision';
import { getApiErrorCode } from '@/util/api-error';
import { paths } from '@/util/paths';
import { mapActions, mapGetters } from 'vuex';
import { viewport } from '@/util/viewport';

export default {
  name: 'PageOpportunityGoNoGo',
  components: {
    BaseAvatar,
    BaseChip,
    BaseIcon,
    BaseMoreActions,
    BaseTag,
    BaseLoadingBar,
    BaseInvitationGoNoGo,
    BaseVoteButton,
    ConfirmationDialog,
    FinalDecisionCard,
    FinalDecisionDialog,
    GoNoGoDialog,
    GoNoGoVoteProgress,
    OpportunityHeader,
  },
  props: {
    opportunityId: String,
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    invitationActions() {
      return [{ key: 'remove', label: this.$t('go-nogo.remove-invitation'), icon: 'trash-2', danger: true }];
    },
    ...mapGetters('auth', ['currentUser', 'canDecideGoNoGo']),
    ...mapGetters('tenantModules', ['tenantModules']),
    ...mapGetters('tenant', ['tenant']),
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('goNoGo', [
      'opportunityVotes',
      'tenantOpportunityVoteReasons',
      'currentUserVote',
      'totalVotes',
      'votesCount',
      'finalDecision',
      'finalDecisionHistory',
      'secondaryVotesLocked',
      'pending',
    ]),
    ...mapGetters('tenant', ['users']),
    ...mapGetters('tenantModules', ['tenantModules']),
    goVotesCount() {
      return this.opportunityVotes.filter((v) => v.vote === 'GO').length;
    },
    nogoVotesCount() {
      return this.opportunityVotes.filter((v) => v.vote === 'NOGO').length;
    },
    goPercentage() {
      if (!this.votesCount) return 0;
      return Math.round((this.goVotesCount / this.votesCount) * 100);
    },
    // Counted, not `100 - goPercentage`, which silently attributed every INDIFFERENT vote to NO-GO.
    // The pair is capped rather than normalised: INDIFFERENT votes legitimately leave a gap, but two
    // independent `.5` roundings (1 GO / 7 NO-GO of 8) would otherwise display 13% + 88% = 101%.
    nogoPercentage() {
      if (!this.votesCount) return 0;
      const nogoPercentage = Math.round((this.nogoVotesCount / this.votesCount) * 100);

      return Math.min(nogoPercentage, 100 - this.goPercentage);
    },
    // The journal outlives a deletion, so the card can be worth showing with no decision recorded.
    showFinalDecisionCard() {
      return !!this.finalDecision || this.finalDecisionHistory.length > 0 || this.canDecideGoNoGo;
    },
    // Sort: current user first, then voted (GO/NOGO mixed in original order),
    // then pending at the bottom — keeps unresolved votes in a calm zone.
    sortedVotes() {
      const isMine = (v) => v.user && this.currentUser && v.user.id === this.currentUser.id;
      const isPending = (v) => v.isPending || !v.vote;
      return [...this.opportunityVotes].sort((a, b) => {
        if (isMine(a) && !isMine(b)) return -1;
        if (!isMine(a) && isMine(b)) return 1;
        if (!isPending(a) && isPending(b)) return -1;
        if (isPending(a) && !isPending(b)) return 1;
        return 0;
      });
    },
  },
  methods: {
    ...mapActions('opportunity', ['getOpportunity']),
    ...mapActions('goNoGo', ['fetchOpportunityVotes', 'deleteOpportunityVote']),
    ...mapActions('goNoGo', [
      'fetchFinalDecision',
      'fetchFinalDecisionHistory',
      'recordFinalDecision',
      'changeFinalDecision',
      'deleteFinalDecision',
    ]),
    ...mapActions('goNoGo', { resetGoNoGo: 'RESET' }),
    getGoNoGoVoteTone,
    partitionVoteReasonsForVote(opportunityVote) {
      return partitionVoteReasons(opportunityVote, this.tenantOpportunityVoteReasons);
    },
    getCustomGoNoGoReason(opportunityVote) {
      const { customReasons } = this.partitionVoteReasonsForVote(opportunityVote);
      return formatCustomVoteReasons(customReasons);
    },
    getPresetVoteReasons(opportunityVote) {
      return this.partitionVoteReasonsForVote(opportunityVote).presetReasons;
    },
    hasVoteReasons(opportunityVote) {
      return hasVoteReasons(opportunityVote);
    },
    getFullName(firstName, lastName) {
      return firstName + ' ' + lastName;
    },
    filteredVotes(votes) {
      return votes.filter((vote) => vote.user !== null);
    },
    openVote() {
      this.$refs.goNoGoDialog.openDialog();
    },
    executeDeleteVote() {
      const { id } = this.currentUserVote;
      this.deleteOpportunityVote({ opportunityId: this.opportunityId, voteId: id, selfVote: true }).catch((error) =>
        this.handleVoteError(error),
      );
    },
    deleteVote() {
      this.$refs.confirmationDialog.openDialog();
    },
    canRemoveInvitation(opportunityVote) {
      return !!opportunityVote.id && !opportunityVote.vote;
    },
    removeInvitation(opportunityVote) {
      if (!this.canRemoveInvitation(opportunityVote)) return;
      return this.deleteOpportunityVote({
        opportunityId: this.opportunityId,
        voteId: opportunityVote.id,
        selfVote: this.isCurrentUser(opportunityVote.user),
      })
        .then(() => this.$toast.success(this.$t('go-nogo.uninvitation-success')))
        .catch((error) => this.handleVoteError(error));
    },
    // A decision recorded while this page was open locks voting behind our back; refetch so the UI
    // catches up with why the vote was refused.
    handleVoteError(error) {
      this.$toast.error(error);
      if (getApiErrorCode(error) === SECONDARY_VOTES_LOCKED) {
        this.refreshFinalDecision();
      }
    },
    // Never rejects: a final-decision fetch failing should cost us the card, not the whole page
    // (`handleError` would bounce a 404 to not-found).
    refreshFinalDecision() {
      const opportunityId = this.opportunityId;
      return Promise.all([
        this.fetchFinalDecision({ opportunityId }),
        this.fetchFinalDecisionHistory({ opportunityId }),
      ]).catch((error) => {
        console.error('Could not load the final decision:', error);
        this.$toast.error(error);
      });
    },
    openFinalDecisionDialog() {
      this.$refs.finalDecisionDialog.openDialog();
    },
    confirmDeleteFinalDecision() {
      this.$refs.deleteFinalDecisionDialog.openDialog();
    },
    async submitFinalDecision({ vote, reasons, comment }) {
      const changing = !!this.finalDecision;
      this.submittingFinalDecision = true;

      try {
        const action = changing ? this.changeFinalDecision : this.recordFinalDecision;
        await action({ opportunityId: this.opportunityId, vote, reasons, comment });
        this.$refs.finalDecisionDialog.closeDialog();
        this.$toast.success(
          this.$t(changing ? 'go-nogo.final-decision.change-success' : 'go-nogo.final-decision.record-success'),
        );
      } catch (error) {
        this.handleFinalDecisionError(error);
      } finally {
        this.submittingFinalDecision = false;
      }
    },
    async executeDeleteFinalDecision() {
      try {
        await this.deleteFinalDecision({ opportunityId: this.opportunityId });
        this.$toast.success(this.$t('go-nogo.final-decision.delete-success'));
      } catch (error) {
        this.handleFinalDecisionError(error);
      }
    },
    async handleFinalDecisionError(error) {
      const code = getApiErrorCode(error);
      // Known codes are localized by the toast plugin; anything else is unexpected.
      if (!code) {
        console.error('Final decision request failed:', error);
      }
      this.$toast.error(error);
      await this.refreshFinalDecision();

      // These two mean the dialog's premise — that a decision exists, or does not — went stale in
      // another session. Left open, its live `finalDecision` prop silently swaps which action the
      // confirm button dispatches, so a second click would overwrite a decision the user never saw.
      if (code === FINAL_DECISION_ALREADY_EXISTS || code === FINAL_DECISION_NOT_FOUND) {
        this.$refs.finalDecisionDialog.closeDialog();
      }
    },
    isCurrentUser(user) {
      return user && user.id === this.currentUser.id;
    },
    async loadOpportunityVotes() {
      if (this.votesLoaded || !this.users || !this.users.length) return;
      this.votesLoaded = true;

      try {
        await this.fetchOpportunityVotes({
          opportunityId: this.opportunityId,
          users: this.users,
          currentUser: this.currentUser,
        });
      } catch (error) {
        this.votesLoaded = false;
        this.handleError(error);
      }
    },
    voterDisplayName(user) {
      return user ? this.getFullName(user.firstName, user.lastName) : this.$t('unknown.user');
    },
    // No "voted vs changed" distinction, unlike the decision card: the row is created by the
    // invitation, so `updatedAt` is already set on a first vote and can't tell the two apart.
    voteDateLabel(opportunityVote) {
      if (opportunityVote.isPending || !opportunityVote.vote) {
        return this.$t('go-nogo.invited-on', { date: formatLongDate(opportunityVote.createdAt) });
      }
      return this.$t('go-nogo.voted-on', {
        date: formatLongDate(opportunityVote.updatedAt || opportunityVote.createdAt),
      });
    },
    handleError(error) {
      // The `return` matters: without it a transport error (no response at all) redirects and then
      // dereferences `error.response.status` on the next line.
      if (!error.response) {
        this.$router.replace(paths.notFound);
        return;
      }

      if (error.response.status === 404 || error.response.status === 403) {
        this.$router.replace(paths.notFound);
      } else {
        console.error(error);
        this.$toast.error(error);
        throw error;
      }
    },
  },
  data() {
    return {
      assignees: [],
      submittingFinalDecision: false,
      votesLoaded: false,
    };
  },
  watch: {
    // The roster arrives asynchronously (auth/validate -> tenant/getTenant), so it isn't in the
    // store yet on a direct load. Reading it in `created` left the vote list silently empty.
    users: {
      immediate: true,
      handler() {
        this.loadOpportunityVotes();
      },
    },
  },
  async created() {
    // The goNoGo store is a singleton and voting from the listing writes to it without clearing it,
    // so without this the previous opportunity's votes flash up as if they were this one's.
    this.resetGoNoGo();

    try {
      const opportunityId = this.opportunityId;
      const needsOpportunity = !this.opportunity || this.opportunity.id !== opportunityId;

      // Concurrent (the decision only needs the id), but both awaited so a failed opportunity fetch
      // reaches `handleError` instead of becoming an unhandled rejection.
      await Promise.all([
        needsOpportunity ? this.getOpportunity({ id: opportunityId }) : Promise.resolve(),
        this.refreshFinalDecision(),
      ]);
    } catch (error) {
      this.handleError(error);
    }
  },
  destroyed() {
    this.resetGoNoGo();
  },
};
</script>

<style lang="scss">
.go-nogo {
  display: flex;
  flex-direction: column;
  min-height: calc(var(--viewport-height) - 56px);
  width: 100%;

  // ── Main column (two stacked cards), aligned with /tasks layout ──
  // 800px compromise: wider than tasks' side-by-side mode (616) so the
  // vote list breathes, narrower than fullscreen (1200) so progress bar
  // and items don't float in white space.
  &__main {
    width: 100%;
    max-width: 800px;
    margin: 20px auto 40px;
    padding: 0 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  // ── Card primitive (matches TasksOverview / TasksCard) ──
  &__overview,
  &__votes-card {
    background-color: $color-white;
    border-radius: $border-radius-root;
    box-shadow: $shadow-default;
  }

  // ── Overview card ──
  &__overview {
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  &__overview-header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  &__title {
    margin: 0;
    font-size: $font-size-xbigger;
    line-height: 24px;
    font-weight: 900;
    color: $color-normal;
    text-align: left;
  }

  &__vote-buttons {
    display: flex;
    gap: 12px;
    border-top: 1px solid $color-border;
    padding-top: 20px;
  }

  // ── Votes card ──
  // Owns the horizontal inset so every separator inside lands on the same rule as the other cards.
  &__votes-card {
    padding: 24px 32px;
  }

  &__votes-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 0 0 16px 0;
    border-bottom: 1px solid $color-border;
    margin-bottom: 8px;

    // The progress bar below carries the separator in the locked state, so the header drops its own.
    &--flush {
      border-bottom: none;
      margin-bottom: 0;
    }
  }

  &__container {
    position: relative;
  }

  &__votes-progress {
    padding: 8px 0 20px;
    border-bottom: 1px solid $color-border;
    margin-bottom: 8px;
  }

  &__prefer-change {
    margin-top: 12px;
    font-size: $font-size-small;
    color: $color-normal-pale;
  }

  &__items {
    display: flex;
    flex-direction: column;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
    padding: 12px 0;
    margin: 0;
    border-bottom: 1px solid $color-border;

    // No hover state: the row isn't clickable, and the journal entries opposite it never had one.

    &:last-child {
      border-bottom: none;
    }

    &--vote {
      justify-content: space-between;
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
    }

    // Indent justification to align with the user name column
    // (avatar md min-width + avatar gap).
    &--justification {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      padding-left: calc(map-get(map-get($aloha-sizes, 'md'), 'min-width') + 12px);
    }

    &--avatar {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: 12px;
      flex: 0 1 auto;
      min-width: 0;
    }

    &--user-info {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    &--user-name-row {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
    }

    &--user-name {
      font-size: $font-size-small;
      font-weight: $font-weight-medium;
      color: $color-normal;
      font-family: $font-family-raleway;
      line-height: 1.3;
    }

    &--user-name--current {
      font-weight: $font-weight-bold;
    }

    &--vote-date {
      font-size: $font-size-xsmall;
      color: $color-neutral;
      line-height: 1.3;
    }

    &--more {
      display: inline-flex;
      align-items: center;
    }

    &--more-icon {
      cursor: pointer;
      color: $color-normal-pale;
      transition: color 200ms ease;

      &:hover {
        color: $color-normal;
      }

      &.disabled {
        cursor: not-allowed;
        opacity: 0.4;

        &:hover {
          color: $color-normal-pale;
        }
      }
    }
  }

  &__item--votes {
    flex: 0 0 auto;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
  }

  &__vote-display {
    display: flex;
    justify-content: flex-end;
  }

  // Custom reason: italic note with a tone-coloured left-border accent
  // so the comment visually belongs to its vote.
  &__custom-reason {
    margin: 0;
    font-size: $font-size-small;
    font-style: italic;
    line-height: 1.5;
    color: $color-normal-pale;
    white-space: pre-wrap;
    word-break: break-word;
    padding-left: 10px;
    border-left: 3px solid $color-border;

    &--go {
      border-left-color: $color-green;
    }

    &--nogo {
      border-left-color: $color-error;
    }
  }

  &__preset-reasons {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 4px;
  }

  // ── Empty state (matches TasksCard pattern) ──
  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 36px;
    gap: 24px;
  }

  &__empty-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: $border-radius-round;
    background-color: $color-neutral-pale;

    .base-icon {
      width: 37px;
      height: 37px;
      color: $color-bluey-gray;
    }
  }

  &__empty-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  &__empty-title {
    font-size: $font-size-root;
    font-weight: 600;
    color: $color-normal;
  }

  &__empty-subtitle {
    font-size: $font-size-small;
    color: $color-normal-pale;
  }

  @media screen and (max-width: 620px) {
    &__main {
      padding: 0 12px;
    }

    &__overview {
      padding: 24px 20px;
    }

    &__votes-card {
      padding: 24px 20px;
    }
  }
}
</style>
